import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import { useTranslation } from "react-i18next";
import Draggable from "react-draggable";
import swal from "sweetalert";
import {
  LoadingSpinner,
  isUrdu,
  convertToDateString,
  convertUTCToLocalTime,
} from "../../constants/const";
import { ApiFinance } from "./ApiFinance";
import { GetLanguageString } from "../helper/Components";
import { GetSelectedLanguage } from "../helper/Method";
import { ApiFund } from "./ApiFund";
import { getMinMaxDates, validateTransactionDate } from "./fiscalYearHelper";
import { MdOutlineAddCircleOutline } from "react-icons/md";
import { TbTrash, TbTrashOff } from "react-icons/tb";

const EditFund = ({
  setModal,
  memberName,
  setMemberFunds,
  editValue,
  unitId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const toggle = () => setModal(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [fundValues, setFundValues] = useState({});
  const [serverError, setServerError] = useState("");
  const [errors, setErrors] = useState({
    date: "",
    month: "",
    year: "",
    receiptNumber: "",
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [infaqHeads, setInfaqHeads] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [infaqRows, setInfaqRows] = useState([{ codeId: "", amount: "" }]);

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  const addRow = () => {
    const defaultCodeId = infaqHeads.length > 0 ? infaqHeads[0].value : "";
    setInfaqRows([...infaqRows, { codeId: defaultCodeId, amount: "" }]);
  };

  const removeRow = (index) => {
    if (infaqRows.length > 1) {
      setInfaqRows(infaqRows.filter((_, i) => i !== index));
    }
  };

  const handleRowChange = (index, field, val) => {
    const newRows = [...infaqRows];
    newRows[index][field] = val;
    setInfaqRows(newRows);

    if (errors.rowErrors && errors.rowErrors[index]) {
      const newErrors = { ...errors };
      newErrors.rowErrors[index][field] = "";
      setErrors(newErrors);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFile(e.target.files[0]);
    }
  };

  useEffect(() => {
    const currentDate = new Date();
    const tempYear = currentDate.getFullYear();
    const tempMonth = currentDate.getMonth() + 1;
    const loadData = async () => {
      try {
        setLoading(true);
        const fundRes = await ApiFund().getById(editValue);
        const fund = fundRes.data;
        setFundValues({
          ...fund,
          date: fund.date ? convertToDateString(fund.date) : null,
          year: fund.year || tempYear,
          month: fund.month || tempMonth,
        });
        setSelectedDate(
          fund.date ? new Date(convertUTCToLocalTime(fund.date)) : null,
        );
        setInitialDate(
          fund.date ? new Date(convertUTCToLocalTime(fund.date)) : null,
        );

        if (fund.entries && fund.entries.length > 0) {
          setInfaqRows(
            fund.entries.map((e) => ({
              codeId: parseInt(e.codeId) || "",
              amount: e.amount || "",
              lineMemo: e.lineMemo || "",
            })),
          );
        } else {
          setInfaqRows([{ codeId: fund.codeId || "", amount: fund.amount || "" }]);
        }

        const headRes = await ApiFinance().GetFinanceSubCategoryAccountsByCode("infaq");
        if (headRes?.data?.length > 0) {
          setInfaqHeads(headRes.data);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error:", err);
        setLoading(false);
      }
    };

    if (editValue && unitId) loadData();
  }, [editValue, unitId]);

  const validate = () => {
    let temp = {};
    temp.month = fundValues.month ? "" : <GetLanguageString props="funds_add_fund_select_month_error" />;
    temp.year = fundValues.year ? "" : <GetLanguageString props="funds_add_fund_select_year_error" />;

    // Receipt number is only required if it is NOT digitally generated
    const isDigitalReceipt =
      fundValues.generateDigitalReceipt ||
      (fundValues.receiptNumber && /[a-zA-Z]/.test(fundValues.receiptNumber));

    temp.receiptNumber =
      isDigitalReceipt || fundValues.receiptNumber
        ? ""
        : <GetLanguageString props="common_field_required_error" />;

    const dateError = validateTransactionDate(selectedDate, initialDate);
    if (dateError) {
      temp.date = t(dateError);
    } else {
      temp.date = selectedDate ? "" : <GetLanguageString props="funds_add_fund_date_paid_error" />;
    }

    if (totalAmount >= 5000) {
      temp.address = fundValues.address && fundValues.address.trim() !== ""
        ? ""
        : <GetLanguageString props="common_field_required_error" />;
    } else {
      temp.address = "";
    }

    temp.memo = fundValues.memo && fundValues.memo.trim() !== ""
      ? ""
      : <GetLanguageString props="common_field_required_error" />;

    // Row validation
    let rowErrors = [];
    let hasRowErrors = false;
    infaqRows.forEach((row, index) => {
      let rowErr = { codeId: "", amount: "" };
      if (!row.codeId) {
        rowErr.codeId = <GetLanguageString props="common_field_required_error" />;
        hasRowErrors = true;
      }
      if (!row.amount || row.amount.toString().trim() === "") {
        rowErr.amount = <GetLanguageString props="common_field_required_error" />;
        hasRowErrors = true;
      } else {
        const val = parseFloat(row.amount);
        if (isNaN(val) || val <= 0) {
          rowErr.amount = t("amount_must_be_greater_than_zero");
          hasRowErrors = true;
        }
      }
      rowErrors[index] = rowErr;
    });
    temp.rowErrors = hasRowErrors ? rowErrors : null;

    setErrors(temp);

    const isMainFieldsValid = Object.keys(temp)
      .filter((k) => k !== "rowErrors")
      .every((k) => temp[k] === "");

    return isMainFieldsValid && !hasRowErrors;
  };

  const handleInputChangeDate = (value) => {
    setSelectedDate(value);
    setErrors((prev) => ({
      ...prev,
      date: "",
    }));
  };

  const handleReceiptNumber = (e) => {
    const { value } = e.target;
    setFundValues({
      ...fundValues,
      receiptNumber: value,
    });
    setErrors((prev) => ({
      ...prev,
      receiptNumber: "",
    }));
  };

  const totalAmount = infaqRows.reduce(
    (sum, row) => sum + (parseFloat(row.amount) || 0),
    0,
  );

  const handleSubmit = () => {
    if (validate()) {
      setLoading(true);

      const formData = new FormData();
      formData.append("Id", editValue);
      formData.append("MemberId", fundValues.memberId ?? "");
      formData.append("Name", fundValues.name ?? "");
      formData.append("UnitId", fundValues.unitId ?? unitId);
      formData.append(
        "Month",
        selectedDate ? selectedDate.getMonth() + 1 : fundValues.month,
      );
      formData.append(
        "Year",
        selectedDate ? selectedDate.getFullYear() : fundValues.year,
      );
      formData.append("Date", convertToDateString(selectedDate));
      formData.append("Amount", totalAmount);
      formData.append("ReceiptNumber", fundValues.receiptNumber ?? "");
      formData.append("BankAccountId", fundValues.bankAccountId ?? 0);
      formData.append(
        "AccountTransactionId",
        fundValues.accountTransactionId || "",
      );
      formData.append("IsRafiq", fundValues.isRafiq ?? false);
      formData.append("IncludeSubUnits", fundValues.includeSubUnits || false);
      formData.append(
        "GenerateDigitalReceipt",
        fundValues.generateDigitalReceipt || false,
      );
      formData.append("Memo", fundValues.memo || "");
      formData.append("Address", totalAmount >= 5000 ? (fundValues.address || "") : (fundValues.address || ""));

      infaqRows.forEach((row, i) => {
        formData.append(`Entries[${i}].CodeId`, row.codeId);
        formData.append(`Entries[${i}].Amount`, row.amount);
        formData.append(
          `Entries[${i}].LineMemo`,
          row.lineMemo || fundValues.memo || "",
        );
      });

      if (uploadedFile) {
        formData.append("file", uploadedFile);
      }

      ApiFund()
        .update(editValue, formData)
        .then((res) => {
          if (res?.data?.statusCode === "300" || res?.data?.StatusCode === 300) {
            const warningMsg = t(res?.data?.message || res?.data?.Message) || "A modification request for this transaction is already pending approval.";
            swal({
              title: warningMsg,
              className: GetSelectedLanguage() === "en" ? "" : "ur",
              icon: "warning",
              buttons: "OK",
            }).then(() => {
              setModal(false);
            });
            return;
          }

          const resMsg = res?.data?.Message || res?.data?.message;
          const isModification = resMsg === "funds_infaq_amanat_edit_requested";
          const successTitle = isModification
            ? t("funds_infaq_amanat_edit_requested")
            : t("member_member_detail_fund_edited") || "Fund edited successfully";

          swal({
            title: successTitle,
            className: GetSelectedLanguage() === "en" ? "" : "ur",
            icon: "success",
            buttons: "OK",
          });

          if (typeof onSuccess === "function") {
            onSuccess();
          }
          setModal(false);
        })
        .catch((err) => {
          const rawErr = err?.response?.data;
          let errMsg = "";
          if (rawErr) {
            if (typeof rawErr === "string") {
              errMsg = rawErr;
            } else if (rawErr.msg) {
              errMsg = rawErr.msg;
            } else if (rawErr.message) {
              errMsg = rawErr.message;
            } else {
              errMsg = JSON.stringify(rawErr);
            }
          } else {
            errMsg = err?.message || (typeof err === "string" ? err : "Something went wrong");
          }

          if (errMsg.length < 150) {
            setServerError(errMsg);
          }
          swal({
            title: errMsg,
            icon: "error",
            buttons: "OK",
          });
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const isReceiptNoDisabled =
    fundValues.generateDigitalReceipt ||
    (fundValues.receiptNumber && /[a-zA-Z]/.test(fundValues.receiptNumber));

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} handle=".modal-header" cancel=".no-drag">
        <Modal
          isOpen
          className="inmodal"
          autoFocus={false}
          size="xl"
        >
          <ModalHeader className="modal-title" tag="h4">
            {<GetLanguageString props="edit_infaq" />}
            <p className="ur" style={{ margin: 0, fontSize: "14px", fontWeight: "normal" }}>
              {memberName}
            </p>
          </ModalHeader>
          {!loading ? (
            <Form>
              <ModalBody className="no-drag">
                {serverError && <h4 className="text-danger">{serverError}</h4>}
                <Row className="gy-3">
                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="unit" />}
                      </Label>
                      <Input
                        type="text"
                        disabled
                        className={isUrdu(fundValues.unitName || "") ? "ur" : ""}
                        value={fundValues.unitName || ""}
                        placeholder="Unit"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="rafiq" />}
                      </Label>
                      <Input
                        type="text"
                        disabled
                        className={isUrdu(memberName || fundValues.name || "") ? "ur" : ""}
                        value={memberName || fundValues.name || ""}
                        placeholder="Rafiq"
                      />
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="transactions_memo" />}
                      </Label>
                      <Input
                        type="text"
                        className={isUrdu(fundValues.memo || "") ? "ur" : ""}
                        value={fundValues.memo || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFundValues({ ...fundValues, memo: val });
                          if (val && val.trim() !== "") {
                            setErrors((prev) => ({ ...prev, memo: "" }));
                          }
                        }}
                        placeholder="Memo..."
                        invalid={!!errors.memo}
                      />
                      {errors.memo && (
                        <h4 className="text-error">{errors.memo}</h4>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_add_fund_date_paid" />}
                      </Label>
                      <DatePicker
                        placeholderText="dd/mm/yyyy"
                        className="form-control"
                        dateFormat="dd/MM/yyyy"
                        selected={selectedDate}
                        minDate={getMinMaxDates(selectedDate, initialDate).minDate}
                        maxDate={getMinMaxDates(selectedDate, initialDate).maxDate}
                        onChange={handleInputChangeDate}
                      />
                      {errors.date && <h4 className="text-error">{errors.date}</h4>}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="funds_receipt_number" />}
                      </Label>
                      <Input
                        type="text"
                        value={fundValues.receiptNumber || ""}
                        onChange={handleReceiptNumber}
                        disabled={isReceiptNoDisabled}
                        placeholder={isReceiptNoDisabled ? "Digitally generated" : "Receipt number"}
                      />
                      {isReceiptNoDisabled && (
                        <small className="text-info d-block mt-1" style={{ fontSize: "11px", fontWeight: "bold" }}>
                          <i className="fa fa-info-circle" style={{ marginRight: "4px" }}></i>
                          {<GetLanguageString props="funds_digitally_generated_receipt" />}
                        </small>
                      )}
                      {errors.receiptNumber && (
                        <h4 className="text-error">{errors.receiptNumber}</h4>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="col-form-label">
                        {<GetLanguageString props="finance_other_income_total_amount" />}
                      </Label>
                      <Input
                        type="text"
                        disabled
                        value={totalAmount}
                      />
                    </FormGroup>
                  </Col>

                  {totalAmount >= 5000 && (
                    <Col md="12">
                      <FormGroup>
                        <Label className="col-form-label">
                          {<GetLanguageString props="member_address" />} <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          className={isUrdu(fundValues.address || "") ? "ur" : ""}
                          value={fundValues.address || ""}
                          onChange={(e) =>
                            setFundValues({ ...fundValues, address: e.target.value })
                          }
                          placeholder={t("member_address")}
                        />
                        {errors.address && <h4 className="text-error">{errors.address}</h4>}
                      </FormGroup>
                    </Col>
                  )}

                  {/* FILE ATTACHMENT & MODIFICATION REQUEST BANNER */}
                  {fundValues.accountTypeCode === "amanat_account" && (
                    <>
                      <Col md="12">
                        <div style={{ background: "#FFF8E1", border: "1px solid #FFD54F", borderRadius: "8px", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
                          <i className="fa fa-info-circle" style={{ color: "#F57F17", fontSize: "18px" }}></i>
                          <span style={{ fontSize: "13px", color: "#5D4037" }}>
                            {t("amanat_edit_modification_notice")}
                          </span>
                        </div>
                      </Col>
                      <Col md="12">
                        <FormGroup style={{ background: "#F9FAFB", padding: "16px", borderRadius: "8px", border: "1px dashed #D1D5DB" }}>
                          <Label className="col-form-label" style={{ fontWeight: 600, color: "#374151" }}>
                            {<GetLanguageString props="deposit_slip_attachment" />}
                          </Label>
                          <Input
                            type="file"
                            onChange={handleFileChange}
                            accept="image/*,application/pdf"
                            style={{ fontSize: "13px" }}
                          />
                          {fundValues.fileId && (
                            <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "6px" }}>
                              <span className="badge bg-success-light text-success" style={{ padding: "4px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 600 }}>
                                <i className="fa fa-check-circle" style={{ marginRight: "4px" }}></i>
                                {<GetLanguageString props="deposit_slip_already_attached" />}
                              </span>
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </>
                  )}

                  {/* MULTI ROW ENTRIES IN IBOX STYLE */}
                  <Col md="12">
                    <div className="ibox mt-4" style={{ border: "1px solid #E7EAEC", borderRadius: "4px" }}>
                      <div className="ibox-title d-flex justify-content-between align-items-center" style={{ background: "#F3F3F4", borderBottom: "1px solid #E7EAEC", padding: "10px 15px" }}>
                        <h5 style={{ margin: 0, fontSize: "14px", fontWeight: 700 }}>
                          {<GetLanguageString props="finance_infaq_entry_details" />}
                        </h5>
                        <MdOutlineAddCircleOutline
                          size={25}
                          color="#3ba147"
                          onClick={addRow}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                      <div className="ibox-content" style={{ padding: "15px" }}>
                        {infaqRows.map((row, index) => (
                          <Row key={index} className="mb-3 align-items-start animate fadeIn">
                            <Col md>
                              <FormGroup>
                                <Label className="col-form-label">
                                  {<GetLanguageString props="fund_account_head" />}
                                </Label>
                                <Select
                                  options={infaqHeads}
                                  value={infaqHeads.find((x) => x.value === row.codeId) || null}
                                  placeholder="Select infaq head"
                                  onChange={(selected) => handleRowChange(index, "codeId", selected ? selected.value : "")}
                                  className="basic-single no-drag"
                                  classNamePrefix="select"
                                />
                                {errors.rowErrors?.[index]?.codeId && (
                                  <h4 className="text-error">{errors.rowErrors[index].codeId}</h4>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md>
                              <FormGroup>
                                <Label className="col-form-label">
                                  {<GetLanguageString props="funds_add_fund_amount" />}
                                </Label>
                                <Input
                                  type="text"
                                  className="no-drag"
                                  value={row.amount || ""}
                                  onChange={(e) => {
                                    let val = e.target.value.replace(/\D/g, "");
                                    handleRowChange(index, "amount", val);
                                  }}
                                />
                                {errors.rowErrors?.[index]?.amount && (
                                  <h4 className="text-error">{errors.rowErrors[index].amount}</h4>
                                )}
                              </FormGroup>
                            </Col>
                            <Col
                              md="1"
                              className="text-center"
                            >
                              <Label className="col-form-label d-block">&nbsp;</Label>
                              {infaqRows.length === 1 ? (
                                <TbTrashOff
                                  size={25}
                                  color="#a72424"
                                  style={{ opacity: 0.5 }}
                                />
                              ) : (
                                <TbTrash
                                  size={25}
                                  color="#bb0e0e"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => removeRow(index)}
                                />
                              )}
                            </Col>
                          </Row>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle} style={{ borderRadius: "6px" }}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color={fundValues.accountTypeCode === "amanat_account" ? "warning" : "primary"} onClick={handleSubmit} style={{ borderRadius: "6px" }}>
                  {fundValues.accountTypeCode === "amanat_account"
                    ? <GetLanguageString props="submit_modification_request" />
                    : <GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default EditFund;
